<template>
    <header class="account-section-header">
        <div class="account-section-header__top">
            <div class="account-section-header__copy">
                <span class="account-section-header__eyebrow">GETAC / YOUR ACCOUNT</span>
                <h1>{{ title }}</h1>
                <p>{{ subtitle }}</p>
            </div>
            <span class="account-section-header__mark" aria-hidden="true">
                <v-icon>{{ icon }}</v-icon>
            </span>
        </div>

        <nav class="account-section-header__nav" aria-label="Your account">
            <router-link
                v-for="item in visibleItems"
                :key="item.route"
                :to="{ name: routeName(item.route) }"
                class="account-section-header__link"
                :class="{ 'account-section-header__link--active': routeName(item.route) === $route.name }"
                :aria-current="routeName(item.route) === $route.name ? 'page' : null"
            >
                {{ item.title.trim() }}
                <span v-if="routeName(item.route) === $route.name" class="account-section-header__active-mark" aria-hidden="true"></span>
            </router-link>
        </nav>
    </header>
</template>

<script>
import Vue from 'vue'
import { mdiAccountCogOutline } from '@mdi/js'

export default Vue.extend({
    name: 'AccountSettingsHeader',
    props: {
        title: { type: String, default: 'Account settings' },
        subtitle: { type: String, default: 'Manage your profile, security, and saved addresses.' },
        items: { type: Array, default: () => [] },
        icon: { type: String, default: mdiAccountCogOutline }
    },
    computed: {
        /** @returns {Array<{ title: string, route: string, disabled?: boolean }>} */
        visibleItems() {
            return this.items.filter((item) => !item.disabled && item.route && item.title)
        }
    },
    methods: {
        routeName(route) {
            return route === 'saved-quotes' ? 'quotes' : route
        }
    }
})
</script>

<style scoped>
.account-section-header {
    position: relative;
    overflow: hidden;
    margin-bottom: 25px;
    padding: 26px 27px 0;
    background: linear-gradient(120deg, rgba(219, 81, 32, 0.12), rgba(255, 255, 255, 0.025) 54%),
        rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-top: 2px solid #db5120;
    border-radius: 14px;
    box-shadow: 0 14px 36px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.035);
}
.account-section-header::after {
    position: absolute;
    top: -100px;
    right: 5%;
    width: 240px;
    height: 200px;
    background: radial-gradient(ellipse, rgba(219, 81, 32, 0.2), transparent 70%);
    content: '';
    pointer-events: none;
}
.account-section-header__top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 21px;
}
.account-section-header__eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.5px;
}
.account-section-header__copy h1 {
    margin: 7px 0 5px;
    color: #f5f6fa;
    font-size: clamp(25px, 3vw, 34px);
    font-weight: 600;
    letter-spacing: -0.8px;
}
.account-section-header__copy p {
    margin: 0;
    color: #adb5c6;
    font-size: 13px;
    line-height: 1.55;
}
.account-section-header__mark {
    display: grid;
    flex: 0 0 52px;
    width: 52px;
    height: 52px;
    color: #ff9a74;
    background: rgba(219, 81, 32, 0.12);
    border: 1px solid rgba(240, 120, 76, 0.25);
    border-radius: 14px;
    place-items: center;
}
.account-section-header__mark .v-icon {
    color: inherit;
    font-size: 27px;
}
.account-section-header__nav {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 5px;
    overflow-x: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.09);
    scrollbar-width: thin;
    scrollbar-color: rgba(240, 120, 76, 0.4) transparent;
}
.account-section-header__link {
    position: relative;
    display: inline-flex;
    min-height: 48px;
    align-items: center;
    flex: 0 0 auto;
    padding: 0 12px;
    color: #aeb5c3;
    font-size: 11px;
    font-weight: 500;
    text-decoration: none;
    text-transform: capitalize;
    transition: color 140ms ease, background-color 140ms ease;
}
.account-section-header__link:hover,
.account-section-header__link--active {
    color: #fff;
}
.account-section-header__link--active {
    background: linear-gradient(180deg, transparent, rgba(219, 81, 32, 0.09));
}
.account-section-header__active-mark {
    position: absolute;
    right: 12px;
    bottom: 0;
    left: 12px;
    height: 2px;
    background: #db5120;
    border-radius: 2px 2px 0 0;
}
.account-section-header__link:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: -3px;
}
@media (max-width: 600px) {
    .account-section-header {
        margin-bottom: 18px;
        padding: 20px 16px 0;
        border-radius: 11px;
    }
    .account-section-header__top {
        padding-bottom: 17px;
    }
    .account-section-header__mark {
        flex-basis: 42px;
        width: 42px;
        height: 42px;
        border-radius: 11px;
    }
    .account-section-header__mark .v-icon {
        font-size: 23px;
    }
    .account-section-header__link {
        min-height: 44px;
        padding: 0 10px;
        font-size: 10px;
    }
}
</style>
